import React, { forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import useTrackEvent from '../hooks/useTrackEvent';
import { stripHtmlToText } from '../utils/navItemUtils';
import {
	DRAWER_EVENT,
	TRACKING_CONFIG,
	WIDGET_STATE
} from '../utils/trackEvent';
import NavItemLabel from './NavItemLabel';

// An item opens a mega menu iff it carries layout columns — URL is not part of
// the check. MUST match FeaturedNavBar's check; if they disagree the trigger ref
// stays null and Escape-dismissal focus-return breaks.
const hasMegaMenu = (item) => item.columns?.length > 0;

const FeaturedNavItem = forwardRef(
	(
		{
			item,
			isActive,
			isMegaMenuOpen,
			onClick,
			onMouseEnter,
			onMouseLeave,
			onBlur
		},
		ref
	) => {
		const activeClass = isActive
			? 'ws-nav-drawer__featured-item--active'
			: '';
		const className = setClassNames([
			'ws-nav-drawer__featured-item',
			activeClass
		]);
		const track = useTrackEvent();

		// Tracked here rather than in FeaturedNavBar because both branches below —
		// mega menu trigger and plain link — live in this component, and the anchor
		// variant has no other handler to hang tracking off.
		const trackClick = useCallback(() => {
			const opensMegaMenu = hasMegaMenu(item);
			const url = item.url ?? null;
			// isMegaMenuOpen is the state *before* this click (matching widgetState's
			// convention). The trigger toggles, so without this branch every close
			// would be counted as an open.
			//
			// `null`, not undefined, for a placeholder item with no outcome to name —
			// see the `featuredItemClick` result notes in the schema doc.
			let result = null;
			if (opensMegaMenu) {
				result = isMegaMenuOpen
					? TRACKING_CONFIG.megaMenuClosedResult
					: TRACKING_CONFIG.megaMenuOpenedResult;
			} else if (url) {
				result = TRACKING_CONFIG.getNavigatedResult(url);
			}

			// CMS labels may carry markup; analytics wants the text the user read.
			const label = stripHtmlToText(item.label);

			track(DRAWER_EVENT.FEATURED_ITEM_CLICK, {
				element: TRACKING_CONFIG.getHeaderEl(label),
				elementCTA: label,
				result,
				// The featured bar sits outside the drawer panel, so it is reachable
				// without the drawer ever having been opened.
				widgetState: WIDGET_STATE.DEFAULT,
				label,
				url,
				// The item's capability, not this click's direction — true on opens
				// and closes alike. `result` carries the direction.
				opensMegaMenu
			});
		}, [track, item, isMegaMenuOpen]);

		if (hasMegaMenu(item)) {
			return (
				<button
					ref={ref}
					type="button"
					className={className}
					aria-haspopup="true"
					aria-expanded={isMegaMenuOpen}
					aria-controls={`mega-menu-${item.id}`}
					onClick={(event) => {
						trackClick();
						onClick?.(item, event);
					}}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onBlur={onBlur}
				>
					<NavItemLabel label={item.label} />
				</button>
			);
		}

		return (
			<a
				ref={ref}
				href={item.url}
				target={item.target || '_self'}
				className={className}
				onClick={trackClick}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onBlur={onBlur}
			>
				<NavItemLabel label={item.label} />
			</a>
		);
	}
);

FeaturedNavItem.displayName = 'FeaturedNavItem';

FeaturedNavItem.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string,
		label: PropTypes.string.isRequired,
		url: PropTypes.string,
		target: PropTypes.string,
		columns: PropTypes.arrayOf(
			PropTypes.shape({
				gridUnits: PropTypes.number,
				navigationItems: PropTypes.arrayOf(PropTypes.shape({}))
			})
		)
	}).isRequired,
	isActive: PropTypes.bool.isRequired,
	isMegaMenuOpen: PropTypes.bool,
	onClick: PropTypes.func,
	onMouseEnter: PropTypes.func,
	onMouseLeave: PropTypes.func,
	onBlur: PropTypes.func
};

FeaturedNavItem.defaultProps = {
	isMegaMenuOpen: false,
	onClick: undefined,
	onMouseEnter: undefined,
	onMouseLeave: undefined,
	onBlur: undefined
};

export default FeaturedNavItem;
